// ============================================================================
// The command center, drawn to the approved designs (item 701, then item 702's
// round-5 demo, tcs-command-center-run-2-demo-2026-10-07.html): its own panel —
// a "TCS · Command center" bar with the tabs and the red count, the list on the
// left, the item on the right in numbered blocks, the morning message below.
//
// Run 2 (item 702) adds the buttons (actions.tsx): Approve, Push back, Park and
// Unpark, Pin, Priority, Merge, and closing feedback in one press. Needs you is
// pinned on top (newest pin first), then the board's order, then parked at the
// bottom — still in view, never counted. Projects open step by step, each step
// with the same card as Needs you.
//
// Item #703 opens Overlaps and the Map, drawn to the same demo's tabs (its
// S.view==='ov' and 'map' branches, rightOv and rightMap) — its layout, words and
// behaviour, never its data: every group, link and count here is read from the
// items' Touches notes. Needs you orders within a priority by how much open work an
// item touches (decision 8 A). Both tabs are read-only; the card an item opens is
// the same card as Needs you, buttons and all. Item #737 adds the ask box under the
// morning message (renderAsk); without it, as in the tests, the box is drawn greyed.
//
// Pure: everything it shows arrives in props, so the tests beside it render it
// without a network. index.tsx does the reading and the writing.
// ============================================================================
import { createContext, Fragment, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { ExternalLink, RefreshCw, X } from 'lucide-react';
import { Skeleton } from '../../shared/components';
import { ForceLight } from '../../shared/force-light';
import { fmtDate } from '../../shared/format';
import type { DemosView, DemoView, FeedbackLinksView, FeedbackRow, ItemsView, Read, RunsView, Whoami, WriteResult } from './api';
import {
  AREAS,
  ageWords,
  draftResolution,
  feedbackWords,
  groupHeading,
  groupWhy,
  isNeedsYou,
  isParked,
  isProject,
  lanes as toLanes,
  linksOf,
  mapView,
  morningMessage,
  movedUp,
  needsYouCount,
  needsYouSections,
  openItemsFor,
  overlaps as toOverlaps,
  projects as toProjects,
  runLine,
  stagePill,
  tierName,
  touchesCounts,
  tierOf,
  whereItIs,
  type BoardItem,
  type ChildStage,
  type LaneEntry,
  type LinkKind,
  type NeedsYouPiles,
  type OverlapGroup,
  type OverlapsView,
  type Project,
  type SentBack,
} from './derive';
import { paragraphs, splitWriteUp, type Inline } from './writeup';
import { FeedbackActions, ItemActions, type FeedbackKind, type WriteFn } from './actions';

export type TabId = 'you' | 'projects' | 'overlaps' | 'map' | 'running' | 'feedback';

export interface CommandCenterViewProps {
  items: Read<ItemsView> | null;
  runs: Read<RunsView> | null;
  feedback: Read<FeedbackRow[]> | null;
  /** Who may press the buttons (decision 11). Null while being read. */
  who?: Read<Whoami> | null;
  /** For each open report, the closed items that name it (decision 7). */
  feedbackLinks?: Read<FeedbackLinksView> | null;
  onWrite?: WriteFn;
  /** Saves a report through the shared feedback write; returns the failure sentence or null. */
  onFeedbackSave?: (row: FeedbackRow, kind: FeedbackKind, text: string, closeItems: number[]) => Promise<string | null>;
  now?: Date;
  initialTab?: TabId;
  /** The "read from the board" time and the Refresh control in the bar. */
  readAt?: string | null;
  reading?: boolean;
  onRefresh?: () => void;
  /** The ask box (item #737), given what needs Ben in the page's order. Greyed when absent. */
  renderAsk?: (needs: BoardItem[]) => ReactNode;
  /** Which items have a demo on the shelf (item #827). Null while being read. */
  demos?: Read<DemosView> | null;
  /** Reads one demo from the shelf. Without it, "See the demo" is not offered. */
  loadDemo?: (item: number) => Promise<Read<DemoView>>;
}

// ----- Demos on the card (item #827, Ben 9 Oct, D2 option C) -----------------------
// The shelf is read once with the board; a card asks for its own demo only when
// "See the demo" is pressed. A context, so every place that draws the card —
// Needs you, Projects, Overlaps, the Map, Running now — offers it the same way.
type DemoAccess = { has: (n: number) => boolean | null; load: (n: number) => Promise<Read<DemoView>>; failure: string | null };
const DemoContext = createContext<DemoAccess | null>(null);

/** Needs you's four piles, in the approved demo's order and words (item #827, D4 C). */
const PILES: { key: keyof NeedsYouPiles; title: string; hint: string }[] = [
  { key: 'merge', title: 'Merge', hint: 'Checked, every check green. One press merges and starts the deploy.' },
  { key: 'calls', title: 'Your calls', hint: 'Business rules, money, spend, security, what people will see.' },
  { key: 'upkeep', title: "The bots' upkeep", hint: 'How to fix the bots and their rulebook.' },
  { key: 'toClose', title: 'Ready to close', hint: "Nothing is left to decide, by the write-up's own finding. Close it, or keep it open." },
];

const noWrite: WriteFn = async () => ({ ok: false, message: 'This page cannot write.' });

// ----- Three panes (item #855, Ben 28 Oct, decision 2 option 1) -------------------------
// Board, details, chat: each can be hidden, with a bar to drag between them. Sizes and
// which are shown are kept on this device, the way the side menu keeps whether a hub is
// collapsed (nav-shell.tsx, 'tcs:nav:hubs') — not with the account: Ben did not extend
// his 8 Oct ruling (TCSD-0462) to this. Hiding all three is prevented. Phone: unchanged.
export type PaneId = 'board' | 'details' | 'chat';
export interface PaneState {
  show: Record<PaneId, boolean>;
  /** Relative widths; only the shown panes' widths count. */
  sizes: Record<PaneId, number>;
}
const PANES_LS_KEY = 'tcs:command-center:panes';
export const PANE_ORDER: PaneId[] = ['board', 'details', 'chat'];
const PANE_LABEL: Record<PaneId, string> = { board: 'Board', details: 'Details', chat: 'Chat' };
export const DEFAULT_PANES: PaneState = { show: { board: true, details: true, chat: true }, sizes: { board: 42, details: 36, chat: 22 } };
const MIN_PANE = 12;

export function loadPanes(): PaneState {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(PANES_LS_KEY);
    if (!raw) return DEFAULT_PANES;
    const s = JSON.parse(raw) as Partial<PaneState>;
    const show = { ...DEFAULT_PANES.show, ...(s.show ?? {}) };
    const sizes = { ...DEFAULT_PANES.sizes, ...(s.sizes ?? {}) };
    // Never a page with nothing on it, whatever was saved.
    if (!PANE_ORDER.some((p) => show[p])) show.board = true;
    return { show, sizes };
  } catch {
    return DEFAULT_PANES;
  }
}

function savePanes(s: PaneState) {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(PANES_LS_KEY, JSON.stringify(s));
  } catch {
    /* a device that keeps nothing still shows the page */
  }
}

/** Hide or show a pane; the last one shown cannot be hidden. */
export function togglePane(s: PaneState, p: PaneId): PaneState {
  const show = { ...s.show, [p]: !s.show[p] };
  if (!PANE_ORDER.some((q) => show[q])) return s;
  return { ...s, show };
}

/** Move the bar between two shown panes: widths trade, neither under the minimum. */
export function dragPanes(s: PaneState, a: PaneId, b: PaneId, delta: number): PaneState {
  const sa = s.sizes[a];
  const sb = s.sizes[b];
  const na = Math.max(MIN_PANE, Math.min(sa + sb - MIN_PANE, sa + delta));
  return { ...s, sizes: { ...s.sizes, [a]: na, [b]: sa + sb - na } };
}

const STAGE_PILL: Record<ChildStage, string> = {
  'needs you': 'border-st-info-border text-st-info-fg',
  parked: 'border-st-warn-border text-st-warn-fg',
  'bot working': 'border-st-ok-border text-st-ok-fg',
  waiting: 'border-st-warn-border text-st-warn-fg',
  done: 'border-tcs-border text-tcs-text-muted',
};

/** Money moving wrong and broken for someone read red, as drawn. */
const isHot = (i: BoardItem) => tierOf(i) <= 1;

/** owner:* labels for anyone but Ben: whose answer the item waits on. */
const others = (i: BoardItem) =>
  i.labels
    .filter((l) => l.startsWith('owner:') && l !== 'owner:ben')
    .map((l) => l.slice(6).replace(/^\w/, (c) => c.toUpperCase()));

function Pill({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2 py-px text-[10.5px] ${className ?? 'border-tcs-border text-tcs-text-muted'}`}>
      {children}
    </span>
  );
}

// ----- Overlaps' marks (item #703), as the 7 Oct demo draws them --------------------

const short = (t: string) => (t.length > 48 ? `${t.slice(0, 46)}…` : t);

/** The demo's "Decide first" badge. */
function First({ short: s }: { short?: boolean }) {
  return (
    <span className="inline-block whitespace-nowrap rounded-full bg-st-info-solid px-2 py-px text-[10.5px] font-bold uppercase tracking-[0.05em] text-st-solid-ink">
      {s ? 'first' : 'Decide first'}
    </span>
  );
}

/** The demo's "touches N open items" mark. Nothing when an item touches nothing. */
function Reach({ n, long }: { n: number; long?: boolean }) {
  if (!n) return null;
  return (
    <span className="inline-block whitespace-nowrap rounded-full border border-st-info-border bg-st-info-bg px-2 py-px text-[11px] font-semibold text-st-info-fg">
      touches {n}
      {long ? ` open item${n === 1 ? '' : 's'}` : ''}
    </span>
  );
}

const KIND: Record<LinkKind, string> = { settles: 'Settles', waits: 'Waits on', shares: 'Shares records' };

/** Each link with its reason, the other item's title and where it is. */
function LinkLines({ links, byNumber }: { links: ReturnType<typeof linksOf>; byNumber: Map<number, BoardItem> }) {
  if (!links.length) return null;
  return (
    <div className="mt-1.5 flex flex-col gap-1">
      {links.map((l) => {
        const o = byNumber.get(l.number);
        return (
          <div key={`${l.kind}-${l.number}`} className="text-[12.5px] leading-snug">
            <span className="inline-block min-w-[88px] text-[10.5px] font-bold uppercase tracking-[0.05em] text-tcs-text-muted">{KIND[l.kind]}</span>
            <b className="text-tcs-text">{o?.title ?? `Item ${l.number}`}</b>{' '}
            {o && <Pill className="border-st-warn-border text-st-warn-fg">{stagePill(o.labels)}</Pill>}
            <br />
            <span className="text-tcs-text-muted">{l.reason}</span>
          </div>
        );
      })}
    </div>
  );
}

/** "N open items have no Touches note yet" — never silently uncounted. */
function NoNoteLine({ items }: { items: BoardItem[] }) {
  if (!items.length) return null;
  return (
    <p className="mt-2 text-[12.5px] text-tcs-text-muted">
      <b className="text-tcs-text">
        {items.length} open item{items.length === 1 ? ' has' : 's have'} no Touches note yet
      </b>{' '}
      and {items.length === 1 ? 'is' : 'are'} not counted: {items.map((i) => short(i.title)).join(' · ')}.
    </p>
  );
}

/**
 * The card's Overlaps block (the demo's ovBlock): what this item touches, with each
 * reason, and the group it decides first in or belongs to.
 */
function OverlapBlock({ item, board, ov, counts }: { item: BoardItem; board: BoardItem[]; ov: OverlapsView | null; counts: Map<number, number> }) {
  const byNumber = new Map(board.map((i) => [i.number, i]));
  const links = linksOf(item.number, board);
  const n = counts.get(item.number) ?? 0;
  const frame = 'mb-2.5 rounded-lg border border-st-info-border bg-st-info-bg px-3 py-2.5';
  const head = 'mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-st-info-fg';
  if (!item.touches && n === 0) {
    return (
      <section className={frame}>
        <h3 className={head}>Overlaps · no Touches note yet</h3>
        <p className="text-[12.5px] text-tcs-text-muted">
          Nothing has said which open items this one touches. The Assessor writes it every Monday morning, and whenever it writes the item up.
        </p>
      </section>
    );
  }
  if (n === 0) {
    return (
      <section className={frame}>
        <h3 className={head}>Overlaps · touches nothing else that's open</h3>
        <p className="text-[12.5px] text-tcs-text-muted">
          {item.parent ? 'It sits under a project, but shares no question or records with the other open items in it.' : 'It stands alone.'}
        </p>
      </section>
    );
  }
  const group: OverlapGroup | undefined = ov?.groups.find((g) => g.items.some((i) => i.number === item.number));
  return (
    <section className={frame}>
      <h3 className={head}>
        Overlaps · touches {n} open item{n === 1 ? '' : 's'}
      </h3>
      {group && group.first?.number === item.number ? (
        <p className="mb-1 text-[12.5px] text-tcs-text">
          <First /> in <b>{groupHeading(group)}</b>. {groupWhy(group, board)}
        </p>
      ) : group ? (
        <p className="mb-1 text-[12.5px] text-tcs-text">
          Part of <b>{groupHeading(group)}</b>.
        </p>
      ) : null}
      <LinkLines links={links} byNumber={byNumber} />
    </section>
  );
}

function H5({ children, className }: { children: ReactNode; className?: string }) {
  return <h3 className={`mb-2 mt-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-tcs-text-muted ${className ?? ''}`}>{children}</h3>;
}

function Loading() {
  return (
    <div className="space-y-2" aria-busy="true">
      <Skeleton className="h-12 rounded-lg" />
      <Skeleton className="h-12 rounded-lg" />
      <Skeleton className="h-12 rounded-lg" />
    </div>
  );
}

/** The bridge's failure, said in words. A blank list would read as "nothing needs you". */
function Failed({ title, message }: { title: string; message: string }) {
  return (
    <div role="alert" className="rounded-lg border border-st-danger-border bg-st-danger-bg p-3 text-[13px] text-tcs-text">
      <div className="font-semibold">{title}</div>
      <div className="mt-0.5">{message}</div>
    </div>
  );
}

function Said({ words }: { words: string }) {
  return <p className="rounded-lg border border-dashed border-tcs-border p-4 text-[13px] text-tcs-text-muted">{words}</p>;
}

function InlineText({ parts }: { parts: Inline[] }) {
  return (
    <>
      {parts.map((p, i) =>
        p.kind === 'bold' ? (
          <b key={i}>{p.text}</b>
        ) : p.kind === 'italic' ? (
          <i key={i}>{p.text}</i>
        ) : p.kind === 'code' ? (
          <code key={i} className="break-all rounded bg-tcs-bg-muted px-1 font-mono text-[12px]">{p.text}</code>
        ) : (
          <Fragment key={i}>{p.text}</Fragment>
        ),
      )}
    </>
  );
}

function Prose({ text }: { text: string }) {
  return (
    <>
      {paragraphs(text).map((p, i) =>
        p.kind === 'p' ? (
          <p key={i} className="mb-1.5 text-[13px] text-tcs-text">
            <InlineText parts={p.parts} />
          </p>
        ) : (
          <ul key={i} className="mb-1.5 ml-4 list-disc text-[13px] text-tcs-text">
            {p.items.map((it, j) => (
              <li key={j} className="mb-1">
                <InlineText parts={it} />
              </li>
            ))}
          </ul>
        ),
      )}
    </>
  );
}

function Block({ heading, children, dashed }: { heading: string; children: ReactNode; dashed?: boolean }) {
  return (
    <section className={`mb-2 rounded-lg border border-tcs-border bg-tcs-bg px-3 py-2.5 ${dashed ? 'border-dashed' : ''}`}>
      <h3 className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-tcs-text-muted">{heading}</h3>
      {children}
    </section>
  );
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

type Picked =
  | { kind: 'item'; number: number }
  | { kind: 'step'; project: number; number: number }
  | { kind: 'feedback'; id: string }
  // Overlaps and the Map (item #703): a group, a part, or an item opened from either.
  | { kind: 'group'; index: number }
  | { kind: 'part'; name: string }
  | { kind: 'card'; number: number; back: Picked }
  | null;

export function CommandCenterView({
  items,
  runs,
  feedback,
  who = null,
  feedbackLinks = null,
  onWrite = noWrite,
  onFeedbackSave,
  now = new Date(),
  initialTab = 'you',
  readAt = null,
  reading = false,
  onRefresh,
  renderAsk,
  demos = null,
  loadDemo,
}: CommandCenterViewProps) {
  const [tab, setTab] = useState<TabId>(initialTab);
  const [picked, setPicked] = useState<Picked>(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [openProject, setOpenProject] = useState<number | null>(null);
  const [log, setLog] = useState<string[]>([]);

  const demoAccess = useMemo<DemoAccess | null>(() => {
    if (!loadDemo) return null;
    const shelf = demos?.ok ? new Set(demos.data.items) : null;
    return {
      has: (n) => (shelf ? shelf.has(n) : null),
      load: loadDemo,
      failure: demos && !demos.ok ? demos.message : null,
    };
  }, [demos, loadDemo]);

  // Running now (item #827, D3 B): a row opens its item's card on the right.
  const openRunning = (n: number) => {
    setPicked({ kind: 'card', number: n, back: null });
    setPhoneOpen(true);
  };
  const runningPicked = tab === 'running' && picked?.kind === 'card' ? picked.number : null;

  const login = who?.ok ? (who.data.login ?? null) : null;
  const board = items?.ok ? items.data.items : null;
  const runList = runs?.ok ? runs.data.runs : null;
  // Item #1013: the sent-back pile needs the runs (a sent-back item with a Builder run
  // still going is being fixed, not waiting). Runs unreadable → read as none going.
  const sections = useMemo(() => needsYouSections(board ?? [], login, runList ?? []), [board, login, runList]);
  const needs = useMemo(() => [...sections.pinned, ...sections.ranked], [sections]);
  const count = needsYouCount(sections);
  const sentBackOf = (n: number) => sections.sentBack.find((s) => s.item.number === n) ?? null;
  const projectList = useMemo(() => (board ? toProjects(board, login) : []), [board, login]);
  const laneSet = useMemo(() => (board && runList ? toLanes(board, runList, now) : null), [board, runList, now]);
  // Item #855 (decision 2, option 1): three panes, each hideable, with bars to drag.
  const [panes, setPanes] = useState<PaneState>(loadPanes);
  useEffect(() => savePanes(panes), [panes]);
  const panesRef = useRef<HTMLDivElement>(null);
  const byNumber = useMemo(() => new Map((board ?? []).map((i) => [i.number, i])), [board]);
  const ready = needs.filter((n) => n.write_up).length;
  // Item #703: what each open item touches, read from the Touches notes.
  const counts = useMemo(() => touchesCounts(board ?? []), [board]);
  const ov = useMemo(() => (board ? toOverlaps(board) : null), [board]);
  const map = useMemo(() => (board ? mapView(board) : null), [board]);
  const moved = useMemo(() => movedUp(sections.ranked), [sections]);
  const decideFirst = useMemo(() => new Set((ov?.groups ?? []).flatMap((g) => (g.first ? [g.first.number] : []))), [ov]);

  const itemsFailure = items && !items.ok ? items.message : null;
  const runsFailure = runs && !runs.ok ? runs.message : null;
  const morning = itemsFailure
    ? itemsFailure
    : board && runsFailure
      ? morningMessage(needs, { unreadable: runsFailure }, sections.parked, counts)
      : board && laneSet
        ? morningMessage(needs, laneSet, sections.parked, counts)
        : null;

  const onDone = (r: WriteResult, title: string) => {
    const t = new Date(r.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    setLog((l) => [`${t} · ${r.wrote} — “${title}”`, ...l].slice(0, 5));
    onRefresh?.();
  };

  // The item on the right: the one picked, else the first with a write-up, as drawn.
  const youList = [...needs, ...sections.sentBack.map((s) => s.item), ...sections.parked];
  const shown =
    (picked?.kind === 'item' ? youList.find((i) => i.number === picked.number) : undefined) ??
    needs.find((i) => i.write_up) ??
    needs[0] ??
    sections.parked[0] ??
    null;

  const TABS: { id: TabId; label: string; disabled?: string }[] = [
    { id: 'you', label: 'Needs you' },
    { id: 'projects', label: 'Projects' },
    { id: 'overlaps', label: 'Overlaps' },
    { id: 'map', label: 'Map' },
    { id: 'running', label: 'Running now' },
    { id: 'feedback', label: 'Feedback' },
  ];

  /**
   * An item opened from Overlaps or the Map. One that is waiting on Ben opens in
   * Needs you, as the demo's Open does; any other opens the same card here, with a
   * way back to the group or part it came from.
   */
  const openItem = (n: number, back: Picked) => {
    const it = byNumber.get(n);
    if (it && (isNeedsYou(it.labels) || isParked(it.labels))) {
      setTab('you');
      setPicked({ kind: 'item', number: n });
    } else {
      setPicked({ kind: 'card', number: n, back });
    }
    setPhoneOpen(true);
  };
  const reachOf = (n: number) => counts.get(n) ?? 0;

  const boardGate = (body: ReactNode, needRuns = false) =>
    items && !items.ok ? (
      <Failed title="The board can't be shown" message={items.message} />
    ) : needRuns && runs && !runs.ok ? (
      <Failed title="The board can't be shown" message={runs.message} />
    ) : items === null || (needRuns && runs === null) ? (
      <Loading />
    ) : (
      body
    );

  const itemRow = (i: BoardItem, parked = false) => (
    <button
      key={i.number}
      type="button"
      onClick={() => {
        setPicked({ kind: 'item', number: i.number });
        setPhoneOpen(true);
      }}
      aria-pressed={shown?.number === i.number}
      className={`mb-1.5 block w-full rounded-lg border px-2.5 py-2 text-left text-[13px] text-tcs-text hover:border-tcs-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-tcs-accent ${
        parked ? 'bg-tcs-bg' : 'bg-tcs-bg-muted'
      } ${shown?.number === i.number ? 'border-tcs-accent' : 'border-tcs-border'}`}
    >
      <div>
        {i.assignees.length > 0 && !parked && <span className="font-semibold text-tcs-accent">Pinned · </span>}
        {decideFirst.has(i.number) && (
          <>
            <First />{' '}
          </>
        )}
        {i.title}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-tcs-text-muted">
        {parked && <Pill className="border-st-warn-border text-st-warn-fg">parked</Pill>}
        {i.labels.includes('stage:ready-to-merge') && <Pill className="border-st-ok-border text-st-ok-fg">ready to merge</Pill>}
        {i.write_up && !parked && <Pill className="border-st-ok-border text-st-ok-fg">write-up ready</Pill>}
        <Pill className={isHot(i) ? 'border-st-danger-border text-st-danger-fg' : undefined}>{tierName(i)}</Pill>
        <Reach n={reachOf(i.number)} long />
        {!parked && moved.get(i.number) ? <span className="text-[11px] font-semibold text-st-info-fg">▲ {moved.get(i.number)} from today</span> : null}
        <span>{i.parent ? i.parent.title : 'Not under a project'}</span>
        {others(i).map((o) => (
          <Pill key={o} className="border-st-warn-border text-st-warn-fg">
            {o}
          </Pill>
        ))}
        <span>{ageWords(i.created_at, now)}</span>
      </div>
    </button>
  );

  // ----- Projects, step by step ----------------------------------------------------
  const stepProject: Project | undefined =
    picked?.kind === 'step' ? projectList.find((p) => p.item.number === picked.project) : undefined;

  function stepNav(p: Project, n: number): ReactNode {
    const k = p.children.findIndex((c) => c.number === n);
    const prev = p.children[k - 1];
    const next = p.children[k + 1];
    const nextNeed = p.children.slice(k + 1).find((c) => c.stage === 'needs you');
    const go = (num: number) => setPicked({ kind: 'step', project: p.item.number, number: num });
    return (
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-1.5 rounded-lg border border-tcs-border bg-tcs-bg-muted px-2 py-1.5 text-[12px]">
        <button type="button" disabled={!prev} onClick={() => prev && go(prev.number)} className="rounded-md border border-tcs-border px-2 py-0.5 disabled:opacity-40">
          ‹ Previous
        </button>
        <span className="text-tcs-text-muted">
          Step {k + 1} of {p.children.length} · {p.item.title}
        </span>
        <span className="flex gap-1.5">
          {nextNeed && (!next || nextNeed.number !== next.number) && (
            <button type="button" onClick={() => go(nextNeed.number)} className="rounded-md border border-tcs-border px-2 py-0.5">
              Next that needs you ›
            </button>
          )}
          <button
            type="button"
            disabled={!next}
            onClick={() => next && go(next.number)}
            className="rounded-md border border-tcs-accent bg-tcs-accent px-2 py-0.5 font-semibold text-tcs-bg disabled:opacity-40"
          >
            Next ›
          </button>
        </span>
      </div>
    );
  }

  const stepDetail = (() => {
    if (picked?.kind !== 'step' || !stepProject) return null;
    const child = stepProject.children.find((c) => c.number === picked.number);
    if (!child) return null;
    const nav = stepNav(stepProject, child.number);
    const item = byNumber.get(child.number);
    if (!item) {
      return (
        <div>
          {nav}
          <h2 className="mb-1 text-[15px] font-semibold text-tcs-text">{child.title}</h2>
          <Block heading="Where it is">
            <p className="text-[13px] text-tcs-text">{whereItIs(child.labels, child.state)}</p>
          </Block>
        </div>
      );
    }
    return (
      <div>
        {nav}
        <ItemDetail key={item.number} item={item} now={now} who={who} login={login} onWrite={onWrite} onDone={onDone} board={board} ov={ov} counts={counts} sentBack={sentBackOf(item.number)} />
      </div>
    );
  })();

  // ----- Feedback ---------------------------------------------------------------------
  const fbRows = feedback?.ok ? feedback.data : [];
  const links = feedbackLinks?.ok ? feedbackLinks.data.links : {};
  const finished = (id: string) => links[id]?.[0] ?? null;
  const fbShown = picked?.kind === 'feedback' ? fbRows.find((f) => f.feedback_id === picked.id) : fbRows[0];

  const feedbackDetail = (() => {
    if (!fbShown) return null;
    const done = finished(fbShown.feedback_id);
    const openItems = board ? openItemsFor(fbShown.feedback_id, board) : [];
    return (
      <div>
        <h2 className="mb-0.5 text-[15px] font-semibold text-tcs-text">{fbShown.title}</h2>
        <div className="mb-2.5 text-[11.5px] text-tcs-text-muted">
          {fbShown.feedback_id} · {fbShown.who} · reported {fmtDate(fbShown.created_at)} · {feedbackWords(fbShown.status)}
        </div>
        <Block heading="What they reported">
          <p className="whitespace-pre-wrap break-words text-[13px] text-tcs-text">{fbShown.description.trim() || 'They left no detail beyond the title.'}</p>
        </Block>
        <Block heading="On the board">
          {openItems.length === 0 && !done && <p className="text-[13px] text-tcs-text-muted">No board item names this report yet.</p>}
          {openItems.map((i) => (
            <p key={i.number} className="text-[13px] text-tcs-text">
              <a href={i.url} target="_blank" rel="noreferrer" className="hover:underline">
                {i.title}
              </a>{' '}
              <Pill>{whereItIs(i.labels).replace(/\.$/, '')}</Pill>
            </p>
          ))}
          {(links[fbShown.feedback_id] ?? []).map((l) => (
            <p key={l.number} className="text-[13px] text-tcs-text">
              <a href={l.url} target="_blank" rel="noreferrer" className="hover:underline">
                {l.title}
              </a>{' '}
              <Pill className="border-st-ok-border text-st-ok-fg">finished{l.closed_at ? ` ${fmtDate(l.closed_at)}` : ''}</Pill>
            </p>
          ))}
        </Block>
        {done && (
          <div className="mb-2 rounded-lg border border-st-ok-border bg-st-ok-bg px-3 py-2 text-[13px] text-tcs-text">
            <b>Ready to close.</b> “{done.title}” finished{done.closed_at ? ` ${fmtDate(done.closed_at)}` : ''}. Complete opens with a draft taken from its
            last comment; edit it or send it as it is.
          </div>
        )}
        {onFeedbackSave ? (
          <FeedbackActions
            key={fbShown.feedback_id}
            feedbackId={fbShown.feedback_id}
            reporter={fbShown.who}
            draft={done ? draftResolution(done.last_comment?.body, done.title) : null}
            openItems={openItems}
            who={who}
            onSave={(kind, text, closeItems) => onFeedbackSave(fbShown, kind, text, closeItems)}
          />
        ) : null}
      </div>
    );
  })();

  // ----- Overlaps and the Map (item #703) — the 7 Oct demo's rightOv and rightMap --------
  const cardBack = (back: Picked, label: string) => (
    <div className="mb-2.5 flex items-center gap-2 rounded-lg border border-tcs-border bg-tcs-bg-muted px-2 py-1.5 text-[12px]">
      <button type="button" onClick={() => setPicked(back)} className="rounded-md border border-tcs-border px-2 py-0.5">
        ‹ Back
      </button>
      <span className="text-tcs-text-muted">{label}</span>
    </div>
  );

  const pickedCard = (() => {
    if (picked?.kind !== 'card') return null;
    const item = byNumber.get(picked.number);
    if (!item) return null;
    const label =
      picked.back?.kind === 'group' && ov?.groups[picked.back.index]
        ? groupHeading(ov.groups[picked.back.index])
        : picked.back?.kind === 'part'
          ? picked.back.name
          : '';
    return (
      <div>
        {cardBack(picked.back, label)}
        <ItemDetail key={item.number} item={item} now={now} who={who} login={login} onWrite={onWrite} onDone={onDone} board={board} ov={ov} counts={counts} sentBack={sentBackOf(item.number)} />
      </div>
    );
  })();

  const ovGroupIndex = picked?.kind === 'group' ? picked.index : 0;
  const ovDetail = (() => {
    if (pickedCard) return pickedCard;
    const g = ov?.groups[ovGroupIndex];
    if (!g || !board) return null;
    const inGroup = new Set(g.items.map((i) => i.number));
    return (
      <div>
        <h2 className="mb-0.5 text-[15px] font-semibold text-tcs-text">{groupHeading(g)}</h2>
        <div className="mb-2.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-tcs-text-muted">
          <span>{g.items.length} open items</span>
          <span>read-only</span>
        </div>
        <Block heading={g.first ? 'Decide first' : 'Why they are together'}>
          {g.first && (
            <p className="mb-1.5 text-[13px] text-tcs-text">
              <First /> <b>{g.first.title}</b>
            </p>
          )}
          <p className="text-[13px] text-tcs-text">{groupWhy(g, board)}</p>
        </Block>
        <Block heading="The items">
          <div className="mt-1 flex flex-col gap-1.5">
            {g.items.map((it) => (
              <div key={it.number} className="rounded-lg border border-tcs-border bg-tcs-bg-muted px-2.5 py-1.5 text-[12.5px]">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-tcs-text">
                    {g.first?.number === it.number && (
                      <>
                        <First short />{' '}
                      </>
                    )}
                    {it.title} <Pill className={isNeedsYou(it.labels) ? 'border-tcs-accent text-tcs-accent-ink' : 'border-st-warn-border text-st-warn-fg'}>{stagePill(it.labels)}</Pill>{' '}
                    <Reach n={reachOf(it.number)} />
                  </span>
                  <button type="button" onClick={() => openItem(it.number, { kind: 'group', index: ovGroupIndex })} className="shrink-0 rounded-md border border-tcs-border px-2 py-0.5 text-[12px] text-tcs-text">
                    Open
                  </button>
                </div>
                <LinkLines links={linksOf(it.number, board).filter((l) => inGroup.has(l.number))} byNumber={byNumber} />
              </div>
            ))}
          </div>
        </Block>
        <Block heading="What the coordination code did" dashed>
          <p className="text-[13px] text-tcs-text">
            Read each item's Touches note, matched them, and marked {g.first ? 'the one to decide first' : 'them to build together'}. In Needs you it
            ranks the items that touch more open work ahead, inside their own priority. It changed no priority of yours.
          </p>
        </Block>
      </div>
    );
  })();

  const mapDetail = (() => {
    if (pickedCard) return pickedCard;
    const counted = (
      <Block heading="How it is counted" dashed>
        <p className="text-[13px] text-tcs-text">
          From each item's Touches note: the parts of the app it names. A part opens to its open items, and each opens its card, the same one as Needs you.
        </p>
      </Block>
    );
    const part = picked?.kind === 'part' && map ? [...map.areas.flatMap((a) => a.parts.map((p) => ({ ...p, area: a.area }))), ...map.noArea.map((p) => ({ ...p, area: 'No area' }))].find((p) => p.name === picked.name) : null;
    if (part) {
      return (
        <div>
          <h2 className="mb-0.5 text-[15px] font-semibold text-tcs-text">{part.name}</h2>
          <div className="mb-2.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-tcs-text-muted">
            <span>{part.area}</span>
            <span>{part.items.length ? `${part.items.length} open item${part.items.length === 1 ? '' : 's'}` : 'nothing open'}</span>
          </div>
          <Block heading="The items">
            {part.items.length === 0 ? (
              <p className="text-[13px] text-tcs-text-muted">No open item's Touches note names this part.</p>
            ) : (
              <div className="mt-1 flex flex-col gap-1.5">
                {part.items.map((it) => (
                  <div key={it.number} className="flex items-center justify-between gap-2 rounded-lg border border-tcs-border bg-tcs-bg-muted px-2.5 py-1.5 text-[12.5px]">
                    <span className="text-tcs-text">
                      {it.title} <Pill className={isNeedsYou(it.labels) ? 'border-tcs-accent text-tcs-accent-ink' : 'border-st-warn-border text-st-warn-fg'}>{stagePill(it.labels)}</Pill>{' '}
                      <Reach n={reachOf(it.number)} />
                    </span>
                    <button type="button" onClick={() => openItem(it.number, { kind: 'part', name: part.name })} className="shrink-0 rounded-md border border-tcs-border px-2 py-0.5 text-[12px] text-tcs-text">
                      Open
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Block>
          {counted}
        </div>
      );
    }
    return (
      <div>
        <h2 className="mb-0.5 text-[15px] font-semibold text-tcs-text">The system, by your five areas</h2>
        <div className="mb-2.5 text-[11.5px] text-tcs-text-muted">{AREAS.join(' · ')}</div>
        <Block heading="What you're looking at">
          <p className="text-[13px] text-tcs-text">
            Every part of the app belongs to one of your five areas, as you grouped them in September: account management sits in Operations,
            subcontractors in People. An area opens to its parts, each with how many open items touch it.
          </p>
        </Block>
        {counted}
      </div>
    );
  })();

  const left = (
    <>
      {tab === 'you' &&
        boardGate(
          <>
            {log.length > 0 && (
              <div className="mb-2 rounded-lg border border-st-ok-border bg-st-ok-bg px-2.5 py-1.5 text-[12px] text-tcs-text">
                {log.map((l) => (
                  <div key={l}>✓ {l}</div>
                ))}
              </div>
            )}
            <p className="mb-2 text-[11.5px] text-tcs-text-muted">
              Order: priority first. Inside each priority, the items that touch more open work, then oldest. Pinned on top, parked at the bottom.
            </p>
            {count === 0 && <Said words="Nothing needs you. No open item is waiting on a decision or a merge." />}
            {sections.pinned.length > 0 && (
              <>
                <H5>Pinned · newest first</H5>
                {sections.pinned.map((i) => itemRow(i))}
              </>
            )}
            {/* Item #827 (D4 C): the four piles, as drawn in the approved 9 Oct demo. */}
            {PILES.map((p) => (
              <Fragment key={p.key}>
                {sections.piles[p.key].length > 0 || p.key === 'toClose' ? (
                  <>
                    <H5>
                      {p.title} · {sections.piles[p.key].length}
                    </H5>
                    <p className="-mt-1 mb-1.5 text-[11px] text-tcs-text-muted">{p.hint}</p>
                    {sections.piles[p.key].map((i) => itemRow(i))}
                  </>
                ) : null}
                {/* Item #1013 (option A, 28 Oct): the sent-back pile, after Merge, as drawn in its demo. */}
                {p.key === 'merge' && sections.sentBack.length > 0 && (
                  <>
                    <H5>Sent back by the checker · {sections.sentBack.length}</H5>
                    <p className="-mt-1 mb-1.5 text-[11px] text-tcs-text-muted">
                      The checker said not ready, or the Builder stopped without pushing its work. Nothing is working on these until you press.
                    </p>
                    {sections.sentBack.map((s) => (
                      <button
                        key={s.item.number}
                        type="button"
                        onClick={() => {
                          setPicked({ kind: 'item', number: s.item.number });
                          setPhoneOpen(true);
                        }}
                        aria-pressed={shown?.number === s.item.number}
                        className={`mb-1.5 block w-full rounded-lg border-l-4 border px-2.5 py-2 text-left text-[13px] text-tcs-text hover:border-tcs-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-tcs-accent ${
                          s.checksOnly ? 'border-l-tcs-border' : s.kind === 'builder' ? 'border-l-st-danger-border' : 'border-l-st-warn-border'
                        } ${shown?.number === s.item.number ? 'border-tcs-accent bg-tcs-bg-muted' : 'border-tcs-border bg-tcs-bg-muted'}`}
                      >
                        <div>{s.item.title}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-tcs-text-muted">
                          <Pill className={isHot(s.item) ? 'border-st-danger-border text-st-danger-fg' : undefined}>{tierName(s.item)}</Pill>
                          {s.kind === 'checker' ? (
                            <span>
                              checked {s.passes === 1 ? 'once' : `${s.passes} times`} · last “not ready” {fmtTime(s.at)}
                              {s.sendBacks ? ` · sent back ${s.sendBacks === 1 ? 'once' : `${s.sendBacks} times`}` : ''}
                            </span>
                          ) : (
                            <span>the Builder stopped {fmtTime(s.at)} without pushing its work</span>
                          )}
                        </div>
                        <div className="mt-1 text-[12px] text-tcs-text">
                          {s.checksOnly ? (
                            <>
                              <b>Nothing in the work to fix.</b> {s.findings[0] ?? 'One automatic check did not run.'} — the checker runs again when the check does.
                            </>
                          ) : (
                            <>
                              <b>{s.findings.length === 1 ? 'One thing to fix.' : s.findings.length ? `${s.findings.length} things to fix.` : 'Not ready.'}</b>{' '}
                              {s.findings.slice(0, 2).join(' · ')}
                              {s.findings.length > 2 ? ` · and ${s.findings.length - 2} more` : ''}
                            </>
                          )}
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </Fragment>
            ))}
            {count > 0 && (
              <p className="mb-1 mt-1 text-[11.5px] text-tcs-text-muted">
                {ready} of {count} have a write-up ready. Those are the ones you can decide from today.
              </p>
            )}
            {sections.parked.length > 0 && (
              <>
                <H5 className="mt-4">Parked · pinned to the bottom · {sections.parked.length}</H5>
                {sections.parked.map((i) => itemRow(i, true))}
              </>
            )}
          </>,
        )}

      {tab === 'projects' &&
        boardGate(
          projectList.length === 0 ? (
            <Said words="No projects: no open item has steps under it." />
          ) : (
            <>
              <H5>Projects · open one, then step through it</H5>
              {projectList.map((p) => {
                const open = openProject === p.item.number;
                const pinned = login ? p.item.assignees.includes(login) : p.item.assignees.length > 0;
                return (
                  <div key={p.item.number} className="mb-1.5 overflow-hidden rounded-lg border border-tcs-border bg-tcs-bg-muted">
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => setOpenProject(open ? null : p.item.number)}
                      className={`flex w-full items-center justify-between gap-2.5 px-2.5 py-2 text-left text-[13px] ${open ? 'border-b border-tcs-border' : ''}`}
                    >
                      <span className="font-semibold text-tcs-text">
                        {pinned && <span className="text-tcs-accent">Pinned · </span>}
                        {p.item.title}
                      </span>
                      <span className="shrink-0 text-[11.5px] text-tcs-text-muted">
                        {p.closed} of {p.total} done · {p.percent}%
                      </span>
                    </button>
                    {open && (
                      <div className="px-2 py-1.5">
                        <ProjectPin item={p.item} pinned={pinned} who={who} onWrite={onWrite} onDone={onDone} />
                        <ul>
                          {p.children.map((c, k) => (
                            <li key={c.number}>
                              <button
                                type="button"
                                onClick={() => {
                                  setPicked({ kind: 'step', project: p.item.number, number: c.number });
                                  setPhoneOpen(true);
                                }}
                                aria-pressed={picked?.kind === 'step' && picked.number === c.number}
                                className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1 text-left text-[12.5px] hover:bg-tcs-surface ${
                                  picked?.kind === 'step' && picked.number === c.number ? 'bg-tcs-surface' : ''
                                }`}
                              >
                                <span className="text-tcs-text">
                                  {k + 1}. {c.title}
                                </span>
                                <span className="flex shrink-0 items-center gap-1">
                                  {c.stage !== 'done' && <Reach n={reachOf(c.number)} />}
                                  <Pill className={STAGE_PILL[c.stage]}>{c.stage}</Pill>
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ),
        )}

      {tab === 'overlaps' &&
        boardGate(
          ov && (
            <>
              <H5>
                Overlaps · {ov.groups.length} group{ov.groups.length === 1 ? '' : 's'} of open items that share a question or records
              </H5>
              {ov.groups.length === 0 && (
                <Said
                  words={
                    ov.alone.length
                      ? 'No two open items share a question or records.'
                      : 'No open item has a Touches note yet, so nothing can be grouped. The Assessor writes them every Monday morning.'
                  }
                />
              )}
              {ov.groups.map((g, k) => {
                const on = picked?.kind !== 'card' && ovGroupIndex === k;
                return (
                  <button
                    key={g.items.map((i) => i.number).join('-')}
                    type="button"
                    aria-pressed={on}
                    onClick={() => {
                      setPicked({ kind: 'group', index: k });
                      setPhoneOpen(true);
                    }}
                    className={`mb-2 block w-full rounded-lg border bg-tcs-bg-muted px-2.5 py-2 text-left text-tcs-text focus:outline-none focus-visible:ring-2 focus-visible:ring-tcs-accent ${
                      on ? 'border-st-info-border ring-1 ring-st-info-border' : 'border-tcs-border'
                    }`}
                  >
                    <b className="mb-0.5 block text-[13px]">{groupHeading(g)}</b>
                    <span className="text-[11.5px] text-tcs-text-muted">
                      {g.items.length} open items{g.first ? ` · decide first: ${short(g.first.title)}` : ' · build together'}
                    </span>
                  </button>
                );
              })}
              {ov.alone.length > 0 && (
                <p className="mt-2 text-[12.5px] text-tcs-text-muted">These touch nothing else that's open: {ov.alone.map((i) => short(i.title)).join(' · ')}.</p>
              )}
              <NoNoteLine items={ov.noNote} />
            </>
          ),
        )}

      {tab === 'map' &&
        boardGate(
          map && (
            <>
              <H5>Map · your five areas of the business · counted from each item's Touches note</H5>
              {map.areas.map((a) => (
                <details key={a.area} className="mb-2 rounded-lg border border-tcs-border bg-tcs-bg-muted">
                  <summary className="flex cursor-pointer flex-wrap justify-between gap-2 px-2.5 py-2 text-[13px]">
                    <b className={`text-[14px] ${a.open >= 5 ? 'text-st-danger-fg' : a.open ? 'text-st-warn-fg' : 'text-tcs-text'}`}>{a.area}</b>
                    <span className="text-tcs-text-muted">
                      {a.open ? `${a.open} open item${a.open === 1 ? '' : 's'}` : 'nothing open'} · {a.parts.length} part{a.parts.length === 1 ? '' : 's'}
                      {a.publicPages ? ` · ${a.publicPages} public page${a.publicPages === 1 ? '' : 's'}` : ''}
                    </span>
                  </summary>
                  {a.parts.map((p) => (
                    <button
                      key={p.name}
                      type="button"
                      aria-pressed={picked?.kind === 'part' && picked.name === p.name}
                      onClick={() => {
                        setPicked({ kind: 'part', name: p.name });
                        setPhoneOpen(true);
                      }}
                      className={`flex w-full justify-between gap-2 border-t border-tcs-border px-2.5 py-1.5 text-left text-[12.5px] text-tcs-text hover:bg-tcs-surface ${
                        picked?.kind === 'part' && picked.name === p.name ? 'bg-tcs-surface' : ''
                      }`}
                    >
                      <span>{p.name}</span>
                      <Pill className={p.items.length ? 'border-tcs-accent text-tcs-accent-ink' : 'border-st-warn-border text-st-warn-fg'}>
                        {p.items.length ? `${p.items.length} open` : 'nothing open'}
                      </Pill>
                    </button>
                  ))}
                </details>
              ))}
              <NoNoteLine items={map.noNote} />
              {map.noArea.length > 0 && (
                <p className="mt-2 text-[12.5px] text-tcs-text-muted">
                  {map.noArea.length} part{map.noArea.length === 1 ? ' of the app names' : 's of the app name'} no area and {map.noArea.length === 1 ? 'is' : 'are'} not counted above:{' '}
                  {map.noArea.map((p) => p.name).join(', ')}.
                </p>
              )}
              {map.unknown.length > 0 && (
                <p className="mt-2 text-[12.5px] text-tcs-text-muted">
                  Notes name {map.unknown.length === 1 ? 'a part' : 'parts'} the catalog doesn't have, so {map.unknown.length === 1 ? 'it is' : 'they are'} not counted:{' '}
                  {map.unknown.map((u) => `${u.name} (${u.items.length})`).join(', ')}.
                </p>
              )}
            </>
          ),
        )}

      {tab === 'running' &&
        boardGate(
          laneSet && (
            <>
              <H5>Running now</H5>
              {/* Item #855 (decision 1 option 1): Ready to run sits between Design and Build and check. */}
              <div className="grid gap-2 lg:grid-cols-4">
                <Lane name="Recon" entries={laneSet.recon} onOpen={openRunning} picked={runningPicked} />
                <Lane name="Design" entries={laneSet.design} onOpen={openRunning} picked={runningPicked} />
                <Lane name="Ready to run" hint="You approved these. Open one for its run line." entries={laneSet.ready} onOpen={openRunning} picked={runningPicked} />
                <Lane name="Build & check" hint="Only work really happening." entries={laneSet.build} onOpen={openRunning} picked={runningPicked} />
              </div>
              <p className="mt-2.5 text-[12px] text-tcs-text-muted">
                Ready to run lists every approved item that is not a project and not yet being built, oldest first; its card carries the run line
                to copy into Cowork. Build and check shows only work really happening — a finished or failed run is on its item, and an item the
                checker sent back is under Needs you. Read from the board and the last 24 hours of Build Room runs.
              </p>
            </>
          ),
          true,
        )}

      {tab === 'feedback' && (
        <>
          {feedback === null && <Loading />}
          {feedback && !feedback.ok && <Failed title="Feedback can't be shown" message={feedback.message} />}
          {feedback?.ok && feedback.data.length === 0 && <Said words="No open feedback. Every report has been closed." />}
          {feedback?.ok && feedback.data.length > 0 && (
            <>
              <H5>Feedback · open reports from people using the app</H5>
              {feedbackLinks && !feedbackLinks.ok && (
                <p role="alert" className="mb-2 text-[12px] text-st-danger-fg">
                  Whether a report's board item has finished could not be read: {feedbackLinks.message}
                </p>
              )}
              {feedback.data.map((f) => {
                const done = finished(f.feedback_id);
                const on = fbShown?.feedback_id === f.feedback_id;
                return (
                  <button
                    key={f.feedback_id}
                    type="button"
                    onClick={() => {
                      setPicked({ kind: 'feedback', id: f.feedback_id });
                      setPhoneOpen(true);
                    }}
                    aria-pressed={on}
                    className={`mb-1.5 block w-full rounded-lg border px-2.5 py-2 text-left text-[13px] text-tcs-text ${
                      done ? 'border-st-ok-border bg-st-ok-bg' : 'bg-tcs-bg-muted'
                    } ${on ? 'border-tcs-accent' : done ? '' : 'border-tcs-border'}`}
                  >
                    {f.title}
                    <div className="mt-0.5 text-[11px] text-tcs-text-muted">
                      {f.who} · reported {fmtDate(f.created_at)} ·{' '}
                      {done ? <b className="text-st-ok-fg">ready to close: “{done.title}” finished</b> : feedbackWords(f.status)}
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </>
      )}
    </>
  );

  // "Nothing is waiting on you" only when the board was READ and nothing is: while
  // it is being read, or when it could not be, the right side says that instead.
  const detail =
    tab === 'you' && shown ? (
      <ItemDetail key={shown.number} item={shown} now={now} who={who} login={login} onWrite={onWrite} onDone={onDone} board={board} ov={ov} counts={counts} sentBack={sentBackOf(shown.number)} />
    ) : tab === 'you' && itemsFailure ? (
      <Failed title="The board can't be shown" message={itemsFailure} />
    ) : tab === 'you' && !board ? (
      <Loading />
    ) : tab === 'projects' && stepDetail ? (
      stepDetail
    ) : tab === 'overlaps' && ovDetail ? (
      ovDetail
    ) : tab === 'map' && mapDetail ? (
      mapDetail
    ) : tab === 'feedback' && feedbackDetail ? (
      feedbackDetail
    ) : tab === 'running' && pickedCard ? (
      pickedCard
    ) : (
      <div>
        <h2 className="text-[15px] font-semibold text-tcs-text">{tab === 'you' ? 'Nothing to show' : tab === 'projects' ? 'Open a project on the left' : '—'}</h2>
        <p className="mt-0.5 text-[11.5px] text-tcs-text-muted">
          {tab === 'you'
            ? 'Nothing is waiting on you.'
            : tab === 'projects'
              ? 'Its steps show in the project’s own order. Press one to open it here, with the same card as Needs you.'
              : 'Details for this tab show on the left.'}
        </p>
      </div>
    );

  const phoneDetail =
    tab === 'you'
      ? shown
        ? detail
        : null
      : tab === 'projects'
        ? stepDetail
        : tab === 'overlaps'
          ? picked?.kind === 'group' || picked?.kind === 'card'
            ? ovDetail
            : null
          : tab === 'map'
            ? picked?.kind === 'part' || picked?.kind === 'card'
              ? mapDetail
              : null
            : tab === 'feedback'
              ? feedbackDetail
              : tab === 'running'
                ? pickedCard
                : null;

  const chat = (
    <div className="border-tcs-border md:mt-2.5 md:border-t md:pt-2.5">
      <div aria-label="The morning message" className="max-w-[95%] rounded-lg border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-[13px] text-tcs-text">
        {morning ?? <Skeleton className="h-4 w-3/4" />}
        <small className="mt-1 block text-[10.5px] text-tcs-text-muted">
          The message that replaces the email.{readAt ? ` Read from the board ${fmtTime(readAt)}.` : ''}
        </small>
      </div>
      {renderAsk ? (
        renderAsk(needs)
      ) : (
        <div className="mt-2 flex gap-1.5">
          <input
            disabled
            aria-label="Ask the command center"
            placeholder="Ask about the board, or tell it what to do"
            className="min-w-0 flex-1 rounded-md border border-tcs-border bg-tcs-bg-muted px-2.5 py-2 text-[13px] text-tcs-text-muted"
          />
        </div>
      )}
    </div>
  );

  return (
    <DemoContext.Provider value={demoAccess}>
    <div className="overflow-hidden rounded-xl border border-tcs-border bg-tcs-surface">
      <div role="tablist" aria-label="Command center" className="flex flex-wrap items-center gap-x-3.5 gap-y-1 border-b border-tcs-border bg-tcs-bg-muted px-3.5 py-2 text-[13px]">
        <span className="font-bold text-tcs-text">TCS · Command center</span>
        {TABS.map((t) => {
          const on = t.id === tab;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={on}
              disabled={!!t.disabled}
              title={t.disabled}
              onClick={() => {
                setTab(t.id);
                setPhoneOpen(false);
                // A group, part or card picked on Overlaps or the Map belongs to that tab.
                if (picked && (picked.kind === 'group' || picked.kind === 'part' || picked.kind === 'card')) setPicked(null);
              }}
              className={`border-b-2 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-tcs-accent disabled:cursor-not-allowed disabled:opacity-50 ${
                on ? 'border-tcs-accent font-semibold text-tcs-text' : 'border-transparent text-tcs-text-muted'
              }`}
            >
              {t.label}
              {t.id === 'you' && board && (
                <span className="ml-1 inline-block rounded-full bg-st-danger-fg px-1.5 align-middle text-[10px] font-semibold text-tcs-bg">{count}</span>
              )}
            </button>
          );
        })}
        <span className="ml-auto flex items-center gap-2 text-[12px] text-tcs-text-muted">
          {/* Item #855 (decision 2 option 1): which panes show, on a computer. The last one shown cannot be hidden. */}
          <span className="hidden items-center gap-1 md:inline-flex" aria-label="Which panes show">
            <span>Show:</span>
            {PANE_ORDER.map((p) => {
              const on = panes.show[p];
              const last = on && PANE_ORDER.filter((q) => panes.show[q]).length === 1;
              return (
                <button
                  key={p}
                  type="button"
                  aria-pressed={on}
                  title={last ? 'The last pane showing stays' : on ? `Hide ${PANE_LABEL[p]}` : `Show ${PANE_LABEL[p]}`}
                  onClick={() => setPanes((s) => togglePane(s, p))}
                  className={`rounded-full border px-2 py-px text-[11px] ${on ? 'border-tcs-border text-tcs-text' : 'border-tcs-border text-tcs-text-muted line-through'}`}
                >
                  {PANE_LABEL[p]}
                </button>
              );
            })}
          </span>
          {readAt && <span>read {fmtTime(readAt)}</span>}
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={reading}
              aria-label="Read the board again"
              className="inline-flex items-center gap-1 rounded-md border border-tcs-border px-2 py-0.5 text-tcs-text-secondary hover:bg-tcs-surface disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${reading ? 'animate-spin' : ''}`} aria-hidden />
              Refresh
            </button>
          )}
        </span>
      </div>

      {/* On a phone: the chat above the list, and an item opens over the page — unchanged (item #855). */}
      <div className="p-3 md:hidden">
        <div className="mb-3">{chat}</div>
        {left}
      </div>
      {/* On a computer: three panes, each hideable, with a bar to drag between them (item #855, decision 2 option 1). */}
      <div className="hidden md:flex md:min-h-[600px] md:max-h-[760px]" ref={panesRef}>
        {PANE_ORDER.filter((p) => panes.show[p]).map((p, k, shownPanes) => {
          const total = shownPanes.reduce((a, q) => a + panes.sizes[q], 0);
          const body = p === 'board' ? left : p === 'details' ? detail : chat;
          return (
            <Fragment key={p}>
              {k > 0 && <PaneBar before={shownPanes[k - 1]} after={p} panesRef={panesRef} total={total} onDrag={(a, b, d) => setPanes((s) => dragPanes(s, a, b, d))} />}
              <div
                data-pane={p}
                className={`min-w-0 overflow-auto p-3${p === 'chat' ? ' flex flex-col justify-end' : ''}`}
                style={{ flex: `0 0 calc(${((panes.sizes[p] / total) * 100).toFixed(2)}% - ${((shownPanes.length - 1) * 7) / shownPanes.length}px)` }}
              >
                {body}
              </div>
            </Fragment>
          );
        })}
      </div>

      {phoneOpen && phoneDetail && (
        <div role="dialog" aria-label="Item" className="fixed inset-0 z-40 overflow-y-auto bg-tcs-bg p-4 md:hidden">
          <div className="mb-2 flex justify-end">
            <button type="button" onClick={() => setPhoneOpen(false)} aria-label="Close" className="rounded p-1 text-tcs-text-muted hover:text-tcs-text">
              <X className="h-5 w-5" />
            </button>
          </div>
          {phoneDetail}
        </div>
      )}
    </div>
    </DemoContext.Provider>
  );
}

/** The bar between two panes: drag it and their widths trade (item #855, decision 2 option 1). */
function PaneBar({
  before,
  after,
  panesRef,
  total,
  onDrag,
}: {
  before: PaneId;
  after: PaneId;
  panesRef: RefObject<HTMLDivElement | null>;
  total: number;
  onDrag: (a: PaneId, b: PaneId, delta: number) => void;
}) {
  const [dragging, setDragging] = useState(false);
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Drag to resize ${PANE_LABEL[before]} and ${PANE_LABEL[after]}`}
      title="Drag to resize"
      onPointerDown={(e) => {
        e.preventDefault();
        const width = panesRef.current?.clientWidth || 1;
        let x0 = e.clientX;
        setDragging(true);
        const move = (ev: PointerEvent) => {
          const delta = ((ev.clientX - x0) / width) * total;
          x0 = ev.clientX;
          onDrag(before, after, delta);
        };
        const up = () => {
          setDragging(false);
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      }}
      className={`relative shrink-0 cursor-col-resize ${dragging ? 'bg-tcs-accent' : 'bg-tcs-border hover:bg-tcs-accent'}`}
      style={{ flex: '0 0 7px' }}
    />
  );
}

function Lane({
  name,
  hint,
  entries,
  onOpen,
  picked,
}: {
  name: string;
  hint?: string;
  entries: LaneEntry[];
  onOpen: (n: number) => void;
  picked: number | null;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-tcs-border bg-tcs-bg-muted p-2">
      <b className="mb-1 block text-[12px] text-tcs-text">
        {name} · {entries.length}
      </b>
      {hint && <div className="-mt-0.5 mb-1 text-[11px] text-tcs-text-muted">{hint}</div>}
      {entries.length === 0 ? (
        <div className="text-[12px] text-tcs-text-muted">Nothing here.</div>
      ) : (
        entries.map((e) => (
          <div key={e.key} className="border-t border-tcs-border py-1 text-[12px] first-of-type:border-t-0">
            {/* Item #827 (D3 B): a row opens its card here. A bare bot run has nothing to open. */}
            {e.number !== null ? (
              <button
                type="button"
                onClick={() => onOpen(e.number as number)}
                aria-pressed={picked === e.number}
                className={`text-left text-tcs-text underline decoration-dotted underline-offset-2 hover:decoration-solid ${picked === e.number ? 'font-semibold' : ''}`}
              >
                {e.title}
              </button>
            ) : (
              <span className="text-tcs-text">{e.title}</span>
            )}{' '}
            · <i className="text-tcs-text-muted">{e.detail}</i>
          </div>
        ))
      )}
    </div>
  );
}

/** Pin works on projects too (decision 6): the pin is on the parent item. */
function ProjectPin({
  item,
  pinned,
  who,
  onWrite,
  onDone,
}: {
  item: BoardItem;
  pinned: boolean;
  who: Read<Whoami> | null;
  onWrite: WriteFn;
  onDone: (r: WriteResult, title: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const blocked = who === null ? 'Checking whether you can act from here…' : !who.ok ? who.message : who.data.canAct ? null : (who.data.reason ?? null);
  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-2 px-2 text-[12px]">
      <button
        type="button"
        disabled={!!blocked || busy}
        title={blocked ?? undefined}
        onClick={async () => {
          setBusy(true);
          setError(null);
          const r = await onWrite({ action: pinned ? 'unpin' : 'pin', item: item.number });
          setBusy(false);
          if (r.ok) onDone(r.data, item.title);
          else setError(r.message);
        }}
        className="rounded-md border border-tcs-border px-2 py-0.5 text-tcs-text disabled:opacity-45"
      >
        {pinned ? 'Unpin project' : 'Pin project'}
      </button>
      {error && <span role="alert" className="text-st-danger-fg">{error}</span>}
    </div>
  );
}

/** The item card (exported for its tests). */
export function ItemDetail({
  item,
  now,
  who,
  login,
  onWrite,
  onDone,
  board = null,
  ov = null,
  counts = new Map(),
  sentBack = null,
}: {
  item: BoardItem;
  now: Date;
  who: Read<Whoami> | null;
  login: string | null;
  onWrite: WriteFn;
  onDone: (r: WriteResult, title: string) => void;
  /** The board, for the Overlaps block (item #703). Without it the block is left out. */
  board?: BoardItem[] | null;
  ov?: OverlapsView | null;
  counts?: Map<number, number>;
  /** Why it is on the sent-back pile (item #1013), when it is. */
  sentBack?: SentBack | null;
}) {
  const w = item.write_up ? splitWriteUp(item.write_up.body) : null;
  const demoAccess = useContext(DemoContext);
  const [showing, setShowing] = useState(false);
  if (showing && demoAccess) {
    return <DemoPane number={item.number} title={item.title} load={demoAccess.load} onBack={() => setShowing(false)} />;
  }
  const parked = item.labels.includes('stage:parked');
  const merge = item.labels.includes('stage:ready-to-merge');
  const approved = item.labels.includes('stage:approved') && !isProject(item);
  const forBen = item.labels.includes('stage:needs-decision') || merge || parked || !!sentBack;
  return (
    <div>
      <h2 className="mb-0.5 text-[15px] font-semibold text-tcs-text">{item.title}</h2>
      <div className="mb-2.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-tcs-text-muted">
        <span>{item.parent ? item.parent.title : 'Not under a project'}</span>
        <Pill className={isHot(item) ? 'border-st-danger-border text-st-danger-fg' : undefined}>{tierName(item)}</Pill>
        {parked && <Pill className="border-st-warn-border text-st-warn-fg">parked</Pill>}
        <span>{ageWords(item.created_at, now)}</span>
        <a href={item.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:text-tcs-text">
          open on GitHub <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
      </div>
      {board && !isProject(item) && <OverlapBlock item={item} board={board} ov={ov} counts={counts} />}
      {parked && (
        <Block heading="Parked · at the bottom of Needs you">
          <p className="text-[13px] text-tcs-text">
            Nothing is working on it. It stays in view, with its priority showing, so a parked problem is never out of sight. Unpark sends it{' '}
            {item.write_up ? 'back into Needs you, where it was waiting.' : 'to recon to be written up.'}
          </p>
        </Block>
      )}
      {merge && !w && (
        <Block heading="Ready to merge">
          <p className="text-[13px] text-tcs-text">A finished build whose checks have run. Merging sends it live.</p>
        </Block>
      )}
      {/* Item #1013: what the checker (or the Builder) said, in plain words, with the press below. */}
      {sentBack && (
        <Block heading={sentBack.kind === 'checker' ? 'Sent back by the checker' : 'The Builder stopped without pushing its work'}>
          <p className="mb-1 text-[12px] text-tcs-text-muted">
            {sentBack.kind === 'checker'
              ? `Checked ${sentBack.passes === 1 ? 'once' : `${sentBack.passes} times`} · last “not ready” ${fmtTime(sentBack.at)}${
                  sentBack.sendBacks ? ` · sent back to the Builder ${sentBack.sendBacks === 1 ? 'once' : `${sentBack.sendBacks} times`}` : ''
                }`
              : `Its run stopped ${fmtTime(sentBack.at)} and pushed no branch, so nothing was opened.`}
            {sentBack.runUrl && (
              <>
                {' '}
                <a href={sentBack.runUrl} target="_blank" rel="noreferrer" className="underline">
                  the run
                </a>{' '}
                is kept 14 days.
              </>
            )}
          </p>
          {sentBack.checksOnly ? (
            <p className="text-[13px] text-tcs-text">
              <b>Nothing in the work to fix.</b> {sentBack.findings[0] ?? 'One automatic check did not run.'} The Builder cannot fix a check, so there is
              nothing to send back; the checker runs again when the check does.
            </p>
          ) : sentBack.kind === 'checker' ? (
            <ol className="list-decimal pl-5 text-[13px] text-tcs-text">
              {sentBack.findings.length ? sentBack.findings.map((f) => <li key={f}>{f}</li>) : <li>The checker said not ready; open the item for its words.</li>}
            </ol>
          ) : (
            <p className="text-[13px] text-tcs-text">{sentBack.findings[0]}</p>
          )}
        </Block>
      )}
      {/* Item #855 (decision 1 option 1): the run line, built from the item, or the prompt a session posted. */}
      {approved && <RunLineBlock item={item} />}
      {!forBen && !approved && (
        <Block heading="Where it is">
          <p className="text-[13px] text-tcs-text">{whereItIs(item.labels)} Nothing is waiting on you here.</p>
        </Block>
      )}
      {w && item.write_up ? (
        <>
          {w.blocks.map((b, i) => (
            <Block key={b.heading + i} heading={`${i + 1} · ${b.heading}`}>
              <Prose text={b.text} />
            </Block>
          ))}
          <Block heading={`${w.blocks.length + 1} · Demo`}>
            <DemoBlock number={item.number} written={w.demo} onSee={() => setShowing(true)} />
          </Block>
          <p className="mb-2 text-[11px] text-tcs-text-muted">
            Written up {fmtDate(item.write_up.created_at)}
            {item.write_up.author ? ` by ${item.write_up.author}` : ''}.
          </p>
        </>
      ) : (
        !merge && (
          <Block heading="No write-up yet" dashed>
            <p className="mb-1.5 text-[13px] text-tcs-text">
              {item.write_up_maybe_older
                ? 'No write-up among the latest 15 comments. If one exists it is older; open the item on GitHub to read it.'
                : `Nothing has written this one up, so there's nothing to approve yet. ${
                    item.labels.includes('stage:needs-decision')
                      ? 'Push back, Park and Pin still work.'
                      : parked
                        ? 'Unpark and Priority still work.'
                        : 'Park and Pin still work.'
                  }`}
            </p>
            {item.body.trim() && (
              <p className="whitespace-pre-wrap break-words text-[13px] text-tcs-text-muted">What the item says: {item.body.trim()}</p>
            )}
          </Block>
        )
      )}
      <ItemActions key={item.number} item={item} who={who} login={login} onWrite={onWrite} onDone={onDone} sentBack={sentBack} />
    </div>
  );
}

/** The run line on an approved item's card: copy it into Cowork (item #855). */
function RunLineBlock({ item }: { item: BoardItem }) {
  const line = runLine(item);
  const [copied, setCopied] = useState<string | null>(null);
  return (
    <Block heading="Run line">
      <p className="mb-1 text-[12px] text-tcs-text-muted">
        {line.from === 'dispatch'
          ? `A session wrote this prompt for the item${item.dispatch ? ` on ${fmtDate(item.dispatch.created_at)}` : ''}. Paste it into Cowork.`
          : 'Built from the item itself. Paste it into Cowork; the write-up, the approval and every comment stay on the item.'}
      </p>
      <pre className="whitespace-pre-wrap break-words rounded-lg border border-tcs-border bg-tcs-bg px-3 py-2 font-sans text-[12.5px] text-tcs-text">{line.text}</pre>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          className="rounded-md border border-tcs-accent bg-tcs-accent px-2.5 py-1 text-[12px] font-semibold text-tcs-bg"
          onClick={() => {
            void (async () => {
              try {
                await navigator.clipboard.writeText(line.text);
                setCopied('Copied.');
              } catch {
                setCopied('Select the text above and copy it — this browser would not copy it for you.');
              }
            })();
          }}
        >
          Copy run line
        </button>
        <a href={item.url} target="_blank" rel="noreferrer" className="rounded-md border border-tcs-border px-2.5 py-1 text-[12px] text-tcs-text">
          Open the write-up
        </a>
        {copied && <span className="text-[12px] text-st-ok-fg">{copied}</span>}
      </div>
    </Block>
  );
}

/** The card's Demo block (item #827): "See the demo" when the shelf holds one. */
function DemoBlock({ number, written, onSee }: { number: number; written: string | null; onSee: () => void }) {
  const access = useContext(DemoContext);
  // No shelf to ask (a page that cannot read one): the write-up's own words, as before.
  if (!access) return written ? <Prose text={written} /> : <p className="text-[13px] text-tcs-text">No demo was made for this one.</p>;
  const has = access.has(number);
  if (has) {
    return (
      <div>
        <button type="button" onClick={onSee} className="rounded-md border border-tcs-accent bg-tcs-accent px-3 py-1 text-[12.5px] font-semibold text-tcs-bg hover:opacity-90">
          See the demo
        </button>
        <p className="mt-1 text-[11.5px] text-tcs-text-muted">Opens here, working. The newest version always replaces the old one, and it never expires.</p>
      </div>
    );
  }
  if (access.failure) {
    return <p className="text-[13px] text-tcs-text">The demo shelf could not be read: {access.failure}</p>;
  }
  if (has === null) return <Skeleton className="h-4 w-1/2" />;
  if (written) {
    return (
      <p className="text-[13px] text-tcs-text">
        The write-up names a demo, but it is not on the shelf. The shelf is rebuilt after every write-up and once a day.
      </p>
    );
  }
  return <p className="text-[13px] text-tcs-text">No demo was made for this one.</p>;
}

/**
 * One demo, working, inside the command center (item #827, D2 C: "testable —
 * clickable, viewable, demonstrable"). The bots' HTML runs in a sandboxed frame
 * with its own blank origin: its scripts run, but it cannot reach the app, its
 * sign-in or its data.
 */
function DemoPane({ number, title, load, onBack }: { number: number; title: string; load: (n: number) => Promise<Read<DemoView>>; onBack: () => void }) {
  const [read, setRead] = useState<Read<DemoView> | null>(null);
  const [full, setFull] = useState(false);
  useEffect(() => {
    let live = true;
    void load(number).then((r) => {
      if (live) setRead(r);
    });
    return () => {
      live = false;
    };
  }, [load, number]);
  const bar = (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <button type="button" onClick={onBack} className="rounded-md border border-tcs-border px-2 py-0.5 text-[12px] text-tcs-text">
        ‹ Back to the write-up
      </button>
      <b className="min-w-0 flex-1 text-[13px] text-tcs-text">{title} — demo</b>
      {read?.ok && (
        <button type="button" onClick={() => setFull(!full)} className="rounded-md border border-tcs-border px-2 py-0.5 text-[12px] text-tcs-text">
          {full ? 'Leave full screen' : 'Full screen'}
        </button>
      )}
    </div>
  );
  // A demo is drawn on white paper, as the bots write them; a dark page behind a
  // demo with no background of its own would hide its black text (ForceLight).
  const frame = read?.ok ? (
    <ForceLight>
      <iframe
        title={`Demo: ${title}`}
        sandbox="allow-scripts allow-forms allow-modals allow-popups"
        srcDoc={read.data.html}
        className={`block w-full rounded-lg border border-tcs-border bg-tcs-surface ${full ? 'h-full' : 'h-[620px]'}`}
      />
    </ForceLight>
  ) : read ? (
    <p className="text-[13px] text-st-danger-fg">The demo could not be opened: {read.message}</p>
  ) : (
    <Skeleton className="h-[620px] w-full" />
  );
  if (full) {
    return (
      <div role="dialog" aria-label={`Demo: ${title}`} className="fixed inset-0 z-50 flex flex-col bg-tcs-surface p-3">
        {bar}
        <div className="min-h-0 flex-1">{frame}</div>
      </div>
    );
  }
  return (
    <div>
      {bar}
      {frame}
    </div>
  );
}

